import { BoatPreset, CannonStats, cannonStatsAt, CannonType, EmblemFitting, SailFitting, scaleAt, Slot } from "./BoatTree";
import { BOAT } from "./Config";
import { packColor, raiseLightness } from "./Settings";
import { hullSize, HullSize } from "./Ships";

/**
 * What a ship *is*, resolved out of the catalogue from the two things that describe one: the
 * **loadout** a captain bought ashore, and the **level** they have climbed to in the water.
 *
 * `config/boat.json` stores a catalogue of boats and guns; a ship is one boat, two guns and a
 * level. That resolution was written once inside [Gunnery.ts](Gunnery.ts) when the guns were
 * the only part of the tree anything read, and it lives here because the client draws the same
 * ship the server shoots with — two copies of "what is this ship" is exactly the drift this
 * repo keeps warning about.
 *
 * It lives in `shared/` because both ends need it: the arena fires the guns, and every client
 * builds the same deck out of the same file. What *is* on the wire is only the loadout and the
 * level — a handful of small numbers — because everything below is derivable from a config both
 * ends already have. See design/Evolution.md and design/Networking.md.
 */

/** Degrees to radians. Slots store degrees because that is what a designer types. */
export const DEG_TO_RAD = Math.PI / 180;

/**
 * What a captain bought and went to sea in: one boat, and a gun in each of its two slots.
 *
 * Every field is an **index into the catalogue** rather than a copy of what it names, for the
 * same reason only the levels used to travel: `config/boat.json` is on both ends already, and a
 * frame name on the wire is a second source of truth for something a client can look up.
 *
 * It is a plain object with no methods because it comes from three places that share nothing —
 * the shop's stored locker, a client's join options, and a `Ship` on the wire — and all three
 * hand it to `resolveFitout` unchanged. See design/Shop.md.
 */
export interface Loadout {
    /** Index into `BOAT.presets`. Out of range resolves to the starter rather than to nothing. */
    preset: number;
    /** Index into `BOAT.cannons` per slot, bow first then stern. Exactly `CANNON_SLOTS` long. */
    cannons: readonly number[];
    /**
     * The captain's colours, packed `0xrrggbb`, and which device they fly. **Optional**, and the
     * only optional thing here: a loadout arrives from a save written before there were colours,
     * from a client a version behind, and from a `Ship` on the wire, and all three should put a
     * boat to sea in the catalogue's default colours rather than fail to resolve. Missing means
     * `BOAT.cosmetics.defaults`, filled in by `sanitiseLoadout` and again by `resolveFitout`.
     *
     * A colour is a free choice and not an index, which is the one place this interface departs
     * from "every field is an index into the catalogue" — there is no palette to be an index into,
     * which is the whole point of the Colours tab. `emblem` is still an index, into
     * `BOAT.cosmetics.emblems`.
     */
    sailTint?: number;
    hullTint?: number;
    /** Index into `BOAT.cosmetics.emblems`. **Anything unknown is 0, `None`** — see `resolveFitout`. */
    emblem?: number;
    emblemTint?: number;
}

/** One occupied slot: where the fitting is bolted, and what is in it. */
export interface Fitted<TFitting> {
    /** Index of this slot in the preset's own list. What a `fire` message names for a gun. */
    index: number;
    /** Where it sits on the deck, in hull-local fractions. */
    slot: Slot;
    /** The fitting itself. */
    fitting: TFitting;
}

/**
 * One gun on the deck: which type it is, and what that type comes to at this ship's level.
 *
 * A gun is the one fitting whose numbers are not on the thing itself. `CannonType` stores what
 * a gun is at level 1 and what each level after that is worth, so `type` alone cannot say how
 * far this gun shoots; `stats` is that formula evaluated at the ship's level, resolved here so
 * it happens once per ship rather than once per shot, and so the arena and the client cannot
 * each evaluate it their own way.
 *
 * `typeIndex` is kept because it is what travels: a `Ship` carries the type per slot, and
 * anything rebuilding a loadout from a fitout — the shop's preview, a test — needs the number
 * rather than the object.
 */
export interface FittedCannon extends Fitted<CannonType> {
    /** Which entry of `BOAT.cannons` this is. What the wire carries. */
    typeIndex: number;
    /** Reach, shot speed, reload, traverse and steal at this ship's level. `cannonStatsAt`. */
    stats: CannonStats;
}

/**
 * One ship as the catalogue describes it. Everything a client needs to draw it and everything
 * the server needs to fire it, in one object, derived once per ship rather than per frame.
 */
export interface Fitout {
    /** Which entry of `BOAT.presets`. What the wire carries. */
    presetIndex: number;
    /** The boat itself: its art, its authored length, its handling and its deck furniture. */
    preset: BoatPreset | null;
    /** How many milestones this life has crossed. The one input growth has. */
    level: number;
    /**
     * How big this ship is drawn, as a multiple of the preset's authored length —
     * `scaleAt(BOAT.scale, level)`. Everything measured on the hull is already multiplied by it;
     * it is carried here for the camera, which is the one consumer that wants the bare number.
     */
    scale: number;
    /** How big that boat is *right now*. Every fitting on it is placed against this. */
    size: HullSize;
    /** The preset's two cannon slots, in deck order: bow, then stern. */
    cannons: readonly FittedCannon[];
    /** The preset's sail slots that carry canvas, in its own order. */
    sails: readonly Fitted<SailFitting>[];
    /**
     * Cruise speed as a multiple of CRUISE_SPEED, and turn rate as a multiple of TURN_RATE.
     * Both come from the preset alone and are fed into `advance()` on both sides. **Neither
     * moves during a life**, which is what makes a preset a decision rather than a starting
     * position — and what lets the client's prediction resolve them once. See
     * design/Evolution.md.
     */
    speed: number;
    turn: number;
    /**
     * How much of the wind this ship's canvas collects, from `rigDrive` below. The third number
     * `advance()` takes, and the only one that comes from the rig rather than the hull.
     */
    drive: number;
    /**
     * The captain's colours, packed `0xrrggbb` and ready for Pixi's `tint`. Already legal:
     * masked to 24 bits, and `sailTint` already lifted to `BOAT.cosmetics.sailLightnessFloor`,
     * so nothing downstream has to remember to.
     *
     * **Nothing in the simulation reads these.** They are on the `Fitout` because `Deck.ts`
     * builds off a fitout and nothing else, and a rival's colours are the point of having them.
     */
    sailTint: number;
    hullTint: number;
    emblemTint: number;
    /** Which entry of `BOAT.cosmetics.emblems` this is. What the wire carries. */
    emblemIndex: number;
    /**
     * The device to draw, or **null for none** — which is what an index this build does not know
     * resolves to as well as index 0. A client a version behind still has to draw a rival, and a
     * stranger flying an unknown device should read as a plain sail rather than as somebody
     * else's skull.
     */
    emblem: EmblemFitting | null;
}

/**
 * A ship, from its loadout and its level. Everything missing or out of range degrades to the
 * starter rather than throwing: a hand-edited catalogue, a stale save file, or a client a
 * version behind should draw a plainer boat instead of an empty patch of sea.
 */
export function resolveFitout(loadout: Loadout, level: number): Fitout {
    const presetIndex = clampIndex(loadout.preset, BOAT.presets.length);
    const preset = BOAT.presets[presetIndex] ?? null;
    const scale = scaleAt(BOAT.scale, level);

    // Resolved once and read twice — the deck needs the list, `advance()` needs what it is worth.
    const sails = fitSails(preset);

    const emblemIndex = knownEmblem(loadout.emblem);
    const emblem = BOAT.cosmetics.emblems[emblemIndex];

    return {
        presetIndex,
        preset,
        level: Math.max(0, Math.floor(level)),
        scale,
        size: hullSize(preset, scale),
        cannons: fitCannons(preset, loadout.cannons, level),
        sails,
        speed: preset?.speed ?? 1,
        turn: preset?.turn ?? 1,
        drive: rigDrive(sails.map((fitted) => fitted.fitting)),
        sailTint: sailTintOf(loadout.sailTint),
        hullTint: tint(loadout.hullTint, BOAT.cosmetics.defaults.hullTint),
        emblemTint: tint(loadout.emblemTint, BOAT.cosmetics.defaults.emblemTint),
        emblemIndex,
        // An entry with no art is the catalogue's own way of saying "no device", so entry 0 and
        // an unknown index arrive at the same null without either being a special case here.
        emblem: emblem && emblem.frame.length > 0 ? emblem : null
    };
}

/**
 * A chosen colour, masked to the 24 bits a colour has. Anything unusable — a missing field off an
 * older save, a string, a NaN — falls back to the catalogue's default for that surface.
 */
function tint(raw: number | undefined, fallback: string): number {
    const value = Number(raw);
    return Number.isFinite(value) ? Math.trunc(value) & 0xffffff : packColor(fallback);
}

/**
 * The same, and then lifted to the lightness floor.
 *
 * **Applied on the way in rather than at the point of drawing.** The floor is a rule about what a
 * sail may be, not about how one is rendered, so a `Fitout` should never hold a colour that
 * breaks it — and the arena applies the same function to what a client sent (design/Shop.md), so
 * a hand-crafted black sail is corrected once, in the state everybody reads, rather than
 * independently by every client that happens to remember.
 */
function sailTintOf(raw: number | undefined): number {
    return raiseLightness(tint(raw, BOAT.cosmetics.defaults.sailTint), BOAT.cosmetics.sailLightnessFloor);
}

/**
 * An emblem index this build can actually draw, **or 0**.
 *
 * Unlike every other index in this file it does not clamp to the nearest entry: clamping a
 * stranger's unknown device to the last one in *our* catalogue would draw them flying somebody
 * else's colours, which is a worse lie than drawing them flying none. It is a lookup rather than
 * an array index for a second reason too — if a device ever becomes an uploaded asset id instead
 * of a catalogue position, only this function changes.
 */
function knownEmblem(raw: number | undefined): number {
    // Missing takes the catalogue's default, the same way a missing colour does; only a value
    // that names nothing this build has falls to `None`.
    const index = Math.floor(Number(raw ?? BOAT.cosmetics.defaults.emblem));
    if (!Number.isFinite(index) || index < 0 || index >= BOAT.cosmetics.emblems.length) return 0;
    return index;
}

/**
 * The two guns on a preset's deck, in slot order.
 *
 * A slot with no type named for it — a short array off a stale client — takes **type 0**, not
 * nothing. A boat that spawned with one gun would be a boat that cannot shoot at half the sea,
 * and no message anywhere would say why; the free gun is the honest degradation, and it is the
 * same rule `coercePreset` follows when it pads a deck plan to two rails.
 *
 * **A gun's level is the captain's level plus one**, and this is the only place that conversion
 * happens. `Ship.level` counts milestones *crossed*, so a fresh captain is at level 0; a gun
 * ladder counts from 1, because level 1 is the gun a type describes rather than a step above it
 * (design/Evolution.md: "a life begins with both guns at level 1"). The scale curve takes the
 * captain's level raw, since its floor is what a fresh captain is drawn at — the two are
 * off-by-one against each other on purpose and neither is the other's bug.
 */
function fitCannons(preset: BoatPreset | null, types: readonly number[], level: number): FittedCannon[] {
    if (!preset || BOAT.cannons.length === 0) return [];

    return preset.cannonSlots.map((slot, index) => {
        const typeIndex = clampIndex(types[index] ?? 0, BOAT.cannons.length);
        const type = BOAT.cannons[typeIndex];

        return { index, slot, fitting: type, typeIndex, stats: cannonStatsAt(type, Math.max(1, Math.floor(level) + 1)) };
    });
}

/**
 * The canvas a preset carries, one sail per slot, in its own order.
 *
 * A rig longer than the slot list simply loses its last entries, and a rig shorter than it
 * leaves the last spars bare. Neither is an error: a catalogue mid-edit is a boat wearing less
 * than it asked for rather than a crash, which is the same bargain every other list in the tree
 * makes.
 */
function fitSails(preset: BoatPreset | null): Fitted<SailFitting>[] {
    if (!preset) return [];

    const fitted: Fitted<SailFitting>[] = [];
    preset.sailSlots.forEach((slot, index) => {
        const sail = preset.rig[index];
        if (sail) fitted.push({ index, slot, fitting: sail });
    });

    return fitted;
}

/** An array index, floored and clamped into `[0, count)`. Anything unusable is 0. */
function clampIndex(raw: number, count: number): number {
    const index = Math.floor(Number(raw));
    if (!Number.isFinite(index) || count <= 0) return 0;
    return Math.min(count - 1, Math.max(0, index));
}

/**
 * What a whole rig is worth, as the multiple of WIND_SPEED its canvas collects.
 *
 * **`speed` is how much of the wind a sail takes, not how fast the ship is.** A flat multiplier
 * would make canvas the boring stat: strictly faster, everywhere, forever. As a share of the
 * wind it widens the gap between sailing well and sailing badly instead — a full rig running
 * with the weather is far quicker than a bare one, and in irons the two are the same boat. See
 * design/Evolution.md.
 *
 * **Each further sail counts half as much as the one before**, best first:
 *
 * ```
 * drive = 1 + SUM (speed_i - 1) * 2^-i
 * ```
 *
 * A rule rather than a table, and it does the job the rule was needed for: a two-sail boat is
 * not twice the ship. Sorting by what each sail is worth means adding *any* sail always helps,
 * and never by more than adding the best one, which stays true even if somebody authors a rig
 * that dips.
 */
export function rigDrive(sails: readonly SailFitting[]): number {
    const worth = sails.map((sail) => sail.speed).sort((a, b) => b - a);

    let drive = 1;
    for (let i = 0; i < worth.length; i++) drive += (worth[i] - 1) * Math.pow(0.5, i);

    return drive;
}

/**
 * A `Loadout` from anything at all: a client's join options, a stored locker, a half-merged
 * config. Every index is floored and clamped into the catalogue, and the gun list is padded and
 * trimmed to the preset's own slot count.
 *
 * **It validates a shape and never an entitlement.** The arena runs this on what a joining client
 * sent and cannot ask whether the boat was paid for — the wallet is the client's until accounts
 * land (design/Shop.md) — so what it guarantees is only that the numbers name things that exist.
 * That is the same rule `name` follows: a label, not a claim (design/Networking.md).
 *
 * It is here rather than in the room because both ends want it. The server runs it on a hostile
 * client; the client runs it on a save file written against a catalogue that has since lost a
 * boat. Two implementations of "is this loadout real" would be two answers, and the one that
 * disagreed would be the server's.
 */
export function sanitiseLoadout(raw: unknown): Loadout {
    const source = (raw ?? {}) as { preset?: unknown; cannons?: unknown } & Partial<Loadout>;
    const preset = clampIndex(Number(source.preset), BOAT.presets.length);
    const slots = BOAT.presets[preset]?.cannonSlots.length ?? 0;
    const asked = Array.isArray(source.cannons) ? source.cannons : [];

    const cannons: number[] = [];
    for (let i = 0; i < slots; i++) cannons.push(clampIndex(Number(asked[i]), BOAT.cannons.length));

    // The colours are settled here as well as in `resolveFitout`, so what the arena stores and
    // hands to every other client is already legal rather than merely legal once drawn. Both go
    // through the same three functions and all three are idempotent, so doing it twice costs a
    // few operations at join and nothing after.
    return {
        preset,
        cannons,
        sailTint: sailTintOf(source.sailTint),
        hullTint: tint(source.hullTint, BOAT.cosmetics.defaults.hullTint),
        emblem: knownEmblem(source.emblem),
        emblemTint: tint(source.emblemTint, BOAT.cosmetics.defaults.emblemTint)
    };
}

/**
 * The loadout a captain has until the shop can say otherwise: **the free boat and two free
 * guns**, which is preset 0 with type 0 in both slots.
 *
 * It is the fallback everywhere rather than only at spawn — a stale save file naming a preset
 * that no longer exists, a hostile client naming preset 900, a test with nothing to say about
 * boats — because the one thing that must never happen is a captain in the sea with no ship
 * resolved. See design/Shop.md on why preset 0 and type 0 cost nothing.
 *
 * A fresh array every call, because a caller may hold on to it for the rest of a life.
 */
export function defaultLoadout(): Loadout {
    return {
        preset: 0,
        cannons: new Array<number>(Math.max(1, BOAT.presets[0]?.cannonSlots.length ?? 2)).fill(0),
        sailTint: sailTintOf(undefined),
        hullTint: tint(undefined, BOAT.cosmetics.defaults.hullTint),
        emblem: knownEmblem(undefined),
        emblemTint: tint(undefined, BOAT.cosmetics.defaults.emblemTint)
    };
}

/**
 * The ship a captain puts to sea in with nothing bought and nothing climbed: the default
 * loadout at level 0.
 *
 * Resolved once, because it is also the boat the attract screen sails and the battery the
 * header opens a battle with.
 */
export const STARTING_FITOUT: Fitout = resolveFitout(defaultLoadout(), 0);
